/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  Autocomplete, TextField, Chip, Stack, Alert, Button, CircularProgress,
  InputAdornment, Checkbox, Tooltip, Tab, Tabs, IconButton,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { LoadingButton } from '@mui/lab';
import { callApi } from '../../utils/apiCall';
import { fetchDentallyTreatments, fetchDentallyAppointmentReasons } from '../../utils/dentallyApi';
import { buildMatchSuggestions } from '../../hooks/useFuzzyMatch';
import Iconify from '../Iconify';
import MappingShell, { MappingShellActions, MappingShellContent, MappingShellTitle } from './MappingShell';

const filter = createFilterOptions();

// ─── AI treatment matching ────────────────────────────────────────────────────

async function fetchAiTreatmentMatches(practiceId, dentallyItems, crmItems) {
  const dentallyList = dentallyItems.map((t) => `${t.id}|${t.name}`).join('\n');
  const crmList = crmItems.map((t) => `${t.id}|${t.name}`).join('\n');

  const systemPrompt = `You are a dental treatment matching assistant. Match Dentally treatments to CRM treatments by semantic meaning.
Dental treatments often have different names for the same thing (e.g. "Scale & Polish" = "Hygiene Appointment", "Composite Restoration" = "White Filling", "Full Arch Implant" = "All-on-4", "Teeth Whitening" = "Tooth Bleaching").
Return ONLY a valid JSON array. No markdown, no explanation, no code blocks.
Each item must have: { "dentally_id": string, "crm_id": number or null, "confidence": integer 0-100 }
Use null for crm_id if there is no reasonable match. Only match when you are reasonably confident.`;

  const userMessage = `DENTALLY TREATMENTS:\n${dentallyList}\n\nCRM TREATMENTS:\n${crmList}\n\nMatch each Dentally treatment to the best CRM treatment. Return JSON array only.`;

  const response = await callApi(
    `/api/v1/practices/${practiceId}/ai/chat/`,
    'POST',
    { system_prompt: systemPrompt, messages: [{ role: 'user', content: userMessage }] },
    false,
    { timeout: 60000 }
  );

  if (!response?.ok) return null;

  const raw =
    response?.data?.choices?.[0]?.message?.content ||
    response?.data?.message?.content ||
    response?.data?.content ||
    response?.data?.response;

  if (!raw || typeof raw !== 'string') return null;

  // Strip markdown code blocks if the model wraps the JSON
  const cleaned = raw.replace(/```(?:json)?\s*/gi, '').replace(/```/g, '').trim();
  try {
    const parsed = JSON.parse(cleaned);
    return Array.isArray(parsed) ? parsed : null;
  } catch { return null; }
}

// ─── AI reason matching ───────────────────────────────────────────────────────

async function fetchAiReasonMatches(practiceId, reasons, crmItems) {
  const reasonList = reasons.map((r, i) => `${i}|${r.name || r.reason || String(r)}`).join('\n');
  const crmList = crmItems.map((t) => `${t.id}|${t.name}`).join('\n');

  const systemPrompt = `You are a dental appointment matching assistant. Match Dentally appointment reasons to CRM treatment names by semantic meaning.
Examples: "Exam" = "Check-up", "Scale & Polish" = "Hygienist", "Orthodontic Consultation" = "Invisalign Consultation", "Whitening" = "Teeth Whitening".
Return ONLY a valid JSON array. No markdown, no explanation, no code blocks.
Each item: { "reason_index": number, "crm_id": number or null, "confidence": integer 0-100 }
Use null for crm_id if no reasonable match. Only match when confident.`;

  const userMessage = `DENTALLY APPOINTMENT REASONS:\n${reasonList}\n\nCRM TREATMENTS:\n${crmList}\n\nMatch each reason to the best CRM treatment. Return JSON array only.`;

  const response = await callApi(
    `/api/v1/practices/${practiceId}/ai/chat/`,
    'POST',
    { system_prompt: systemPrompt, messages: [{ role: 'user', content: userMessage }] },
    false,
    { timeout: 60000 }
  );

  if (!response?.ok) return null;
  const raw =
    response?.data?.choices?.[0]?.message?.content ||
    response?.data?.message?.content ||
    response?.data?.content ||
    response?.data?.response;
  if (!raw || typeof raw !== 'string') return null;
  const cleaned = raw.replace(/```(?:json)?\s*/gi, '').replace(/```/g, '').trim();
  try {
    const parsed = JSON.parse(cleaned);
    return Array.isArray(parsed) ? parsed : null;
  } catch { return null; }
}

// ─── Summary Card ────────────────────────────────────────────────────────────

function SummaryCard({ icon, color, count, label, active, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        flex: 1,
        p: 1.5,
        borderRadius: 1.5,
        cursor: 'pointer',
        border: '1px solid',
        borderColor: active ? color : 'divider',
        bgcolor: active ? `${color}08` : 'background.paper',
        transition: 'all 0.15s ease',
        '&:hover': { borderColor: color, bgcolor: `${color}08` },
        minWidth: 0,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Iconify icon={icon} width={20} sx={{ color, flexShrink: 0 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ lineHeight: 1.2 }}>{count}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap>{label}</Typography>
        </Box>
      </Stack>
    </Box>
  );
}

SummaryCard.propTypes = {
  icon: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  active: PropTypes.bool,
  onClick: PropTypes.func,
};

// ─── Main wizard ─────────────────────────────────────────────────────────────

export default function DentallyTreatmentSyncWizard({ open, onClose, practiceId, inline }) {
  // Inline hosts (Dentally workspace → Treatments tab) are always "open".
  const isOpen = inline || open;
  const [loading, setLoading] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState('');
  const [loadError, setLoadError] = useState(null);
  const [rows, setRows] = useState([]);
  const [crmTreatments, setCrmTreatments] = useState([]);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState(null);
  const [result, setResult] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [activeTab, setActiveTab] = useState(0); // 0=Synced, 1=Needs Action, 2=Not in CRM, 3=Reasons
  const [aiMatchingUsed, setAiMatchingUsed] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [reasonsLoading, setReasonsLoading] = useState(false);
  const [reasonsError, setReasonsError] = useState(null);
  const [reasonMappings, setReasonMappings] = useState({});
  // { [reasonName]: crmTreatmentId | null }
  const [selectedReasons, setSelectedReasons] = useState(new Set());
  const [skippedReasons, setSkippedReasons] = useState(new Set());
  const [applyingReasons, setApplyingReasons] = useState(false);
  const [reasonApplyError, setReasonApplyError] = useState(null);
  const [reasonApplyResult, setReasonApplyResult] = useState(null);
  const [reasonConfidences, setReasonConfidences] = useState({});
  // { [reasonName]: confidence 0-100 }
  const [aiReasonRunning, setAiReasonRunning] = useState(false);
  const [aiReasonUsed, setAiReasonUsed] = useState(false);
  const [initialReasonMappings, setInitialReasonMappings] = useState({});
  // tracks what was saved to API on open (to detect clears)

  // Build rows from Dentally treatments + fuzzy matching (no AI)
  const buildSyncRows = useCallback((dentallyItems, crmItems, existingSuggestions) => {
    const suggestions = existingSuggestions || buildMatchSuggestions(dentallyItems, crmItems);
    return suggestions.map(({ dentallyItem, crmItem, confidence }) => {
      const alreadyLinked = crmItems.find(
        (c) => c.dentally_treatment_id && c.dentally_treatment_id === dentallyItem.id
      );
      if (alreadyLinked) {
        return {
          dentallyId: dentallyItem.id,
          dentallyName: dentallyItem.name,
          crmTreatment: alreadyLinked,
          actionType: 'already_linked',
          alias: '',
          confidence: 100,
        };
      }
      const autoAlias =
        crmItem && crmItem.name.toLowerCase() !== dentallyItem.name.toLowerCase()
          ? dentallyItem.name
          : '';
      return {
        dentallyId: dentallyItem.id,
        dentallyName: dentallyItem.name,
        crmTreatment: crmItem,
        actionType: crmItem ? 'match' : 'create_new',
        alias: autoAlias,
        confidence: confidence || 0,
      };
    });
  }, []);

  // Full Dentally sync: fetch from Dentally API + fuzzy matching (NO AI — AI is manual only)
  const runFullSync = useCallback(async (crmItems) => {
    setResyncing(true);
    setLoadingStatus('Fetching treatments from Dentally…');
    try {
      const dentallyList = await fetchDentallyTreatments(practiceId, { activeOnly: true });
      const dentallyItems = dentallyList.map((t) => ({
        id: String(t.id),
        name: (t.nomenclature || t.name || '').trim(),
      }));

      setLoadingStatus('Matching treatments…');
      const suggestions = buildMatchSuggestions(dentallyItems, crmItems);
      const newRows = buildSyncRows(dentallyItems, crmItems, suggestions);

      setRows(newRows);
      setActiveTab(newRows.some((r) => r.actionType !== 'already_linked') ? 1 : 0);
    } catch {
      setLoadError('An unexpected error occurred during re-sync.');
    } finally {
      setResyncing(false);
      setLoadingStatus('');
    }
  }, [practiceId, buildSyncRows]);

  // AI matching — manual only, runs on existing rows
  const runAiMatching = useCallback(async () => {
    if (!rows.length || !crmTreatments.length) return;
    setResyncing(true);
    setLoadingStatus('Asking AI to suggest matches…');
    try {
      const unmatchedRows = rows.filter((r) => r.actionType !== 'already_linked');
      if (!unmatchedRows.length) return;
      const dentallyItems = unmatchedRows.map((r) => ({ id: r.dentallyId, name: r.dentallyName }));
      const unlinkedCrmItems = crmTreatments.filter((c) => !c.dentally_treatment_id);
      const aiMatches = await fetchAiTreatmentMatches(practiceId, dentallyItems, unlinkedCrmItems);
      if (!aiMatches) return;

      const crmById = Object.fromEntries(crmTreatments.map((c) => [c.id, c]));
      setRows((prev) =>
        prev.map((row) => {
          if (row.actionType === 'already_linked') return row;
          const aiMatch = aiMatches.find((m) => String(m.dentally_id) === row.dentallyId);
          if (!aiMatch || !aiMatch.crm_id) return row;
          const matchedCrm = crmById[aiMatch.crm_id];
          if (!matchedCrm) return row;
          const alias = matchedCrm.name.toLowerCase() !== row.dentallyName.toLowerCase() ? row.dentallyName : '';
          return { ...row, crmTreatment: matchedCrm, actionType: 'match', alias, confidence: aiMatch.confidence ?? 0 };
        })
      );
      setAiMatchingUsed(true);
    } catch {
      // silently fall back
    } finally {
      setResyncing(false);
      setLoadingStatus('');
    }
  }, [rows, crmTreatments, practiceId]);

  // On open: load CRM cached data instantly, then auto-sync if no synced items
  useEffect(() => {
    if (!isOpen || !practiceId) return;
    setLoading(true);
    setLoadingStatus('Loading treatments…');
    setLoadError(null);
    setRows([]);
    setResult(null);
    setApplyError(null);
    setSearch('');
    setSelectedIds(new Set());
    setActiveTab(0);
    setAiMatchingUsed(false);
    setResyncing(false);
    setReasons([]);
    setReasonsError(null);

    // Fetch appointment reasons in parallel (non-blocking)
    setReasonsLoading(true);
    fetchDentallyAppointmentReasons(practiceId)
      .then((data) => setReasons(data))
      .catch(() => setReasonsError('Failed to load appointment reasons from Dentally.'))
      .finally(() => setReasonsLoading(false));

    (async () => {
      try {
        // Load CRM treatments: synced (have dentally id) and unsynced
        // _cb cache-buster prevents callApi's 8s GET deduplication from returning stale data
        const cb = Date.now();
        const [syncedRes, allCrmRes] = await Promise.all([
          callApi(`/api/v1/practices/${practiceId}/treatments/?dentally_treatment_id__isnull=false&ordering=treatment_item__name&limit=500&_cb=${cb}`, 'get'),
          callApi(`/api/v1/practices/${practiceId}/treatments/?enabled=true&ordering=treatment_item__name&limit=500&_cb=${cb}`, 'get'),
        ]);

        if (!allCrmRes.ok) {
          setLoadError('Failed to load CRM treatments.');
          return;
        }

        const crmItems = (allCrmRes.data?.results || []).map((t) => ({
          id: t.id,
          name: t.treatment_item?.name || '',
          dentally_treatment_id: (t.dentally_treatment_id && t.dentally_treatment_id.trim()) || '',
          dentally_appointment_reason: (t.dentally_appointment_reason && t.dentally_appointment_reason.trim()) || null,
        }));
        setCrmTreatments(crmItems);

        const initialMappingsObj = {};
        (allCrmRes.data?.results || []).forEach((t) => {
          if (t.dentally_appointment_reason) {
            initialMappingsObj[t.dentally_appointment_reason] = t.id;
          }
        });
        setReasonMappings(initialMappingsObj);
        setInitialReasonMappings(initialMappingsObj); // same object

        // Build synced rows from CRM cached data
        // Include treatments synced by dentally_treatment_id OR by dentally_appointment_reason
        const treatmentSynced = (syncedRes?.ok ? (syncedRes.data?.results || []) : [])
          .filter((t) => t.dentally_treatment_id && t.dentally_treatment_id.trim() !== '');
        const treatmentSyncedIds = new Set(treatmentSynced.map((t) => t.id));

        // Also include treatments that have a reason mapped but no dentally_treatment_id
        const reasonOnlySynced = (allCrmRes.data?.results || [])
          .filter((t) => t.dentally_appointment_reason && !treatmentSyncedIds.has(t.id));

        const allSynced = [...treatmentSynced, ...reasonOnlySynced];
        const syncedRows = allSynced.map((t) => ({
          dentallyId: t.dentally_treatment_id || `reason_${t.id}`,
          dentallyName: t.dentally_treatment_name || t.dentally_appointment_reason || t.treatment_item?.name || '',
          crmTreatment: {
            id: t.id,
            name: t.treatment_item?.name || '',
            dentally_treatment_id: t.dentally_treatment_id || '',
            dentally_appointment_reason: t.dentally_appointment_reason || null,
          },
          actionType: 'already_linked',
          alias: '',
          confidence: 100,
        }));

        setRows(syncedRows);
        setLoading(false);
        setLoadingStatus('');

        // If no synced treatments AND no reason mappings, auto-trigger full sync (first-time setup)
        if (treatmentSynced.length === 0 && Object.keys(initialMappingsObj).length === 0) {
          runFullSync(crmItems);
        }
      } catch {
        setLoadError('An unexpected error occurred loading data.');
        setLoading(false);
        setLoadingStatus('');
      }
    })();
  }, [isOpen, practiceId]);

  const updateRow = useCallback((dentallyId, updates) => {
    setRows((prev) =>
      prev.map((row) => (row.dentallyId === dentallyId ? { ...row, ...updates } : row))
    );
  }, []);

  // Categorize rows
  const syncedRows = useMemo(() => rows.filter((r) => r.actionType === 'already_linked'), [rows]);
  const needsActionRows = useMemo(() => rows.filter((r) => r.actionType === 'match' || r.actionType === 'skip'), [rows]);
  const notInCrmRows = useMemo(() => rows.filter((r) => r.actionType === 'create_new'), [rows]);

  // Filtered rows for current tab
  const currentTabRows = useMemo(() => {
    let tabRows;
    if (activeTab === 0) tabRows = syncedRows;
    else if (activeTab === 1) tabRows = needsActionRows;
    else tabRows = notInCrmRows;

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      return tabRows.filter((row) =>
        row.dentallyName.toLowerCase().includes(q) ||
        row.crmTreatment?.name?.toLowerCase().includes(q) ||
        row.alias?.toLowerCase().includes(q)
      );
    }
    return tabRows;
  }, [activeTab, syncedRows, needsActionRows, notInCrmRows, search]);

  const handleApply = async () => {
    setApplying(true);
    setApplyError(null);

    // Only apply mappings from the active tab
    let sourceRows = [];
    if (activeTab === 1) sourceRows = needsActionRows;
    else if (activeTab === 2) sourceRows = notInCrmRows;
    const mappings = sourceRows
      .filter((r) => r.actionType !== 'skip' && r.actionType !== 'already_linked')
      .map((r) => {
        if (r.actionType === 'create_new') {
          return { dentally_id: r.dentallyId, create_new: true, name: r.dentallyName };
        }
        return {
          dentally_id: r.dentallyId,
          treatment_id: r.crmTreatment?.id,
          ...(r.alias ? { alias: r.alias } : {}),
        };
      })
      .filter((m) => m.treatment_id || m.create_new);

    try {
      const res = await callApi(
        `/api/v1/practices/${practiceId}/dentally/apply-treatment-mapping/`,
        'post',
        { mappings }
      );
      if (!res.ok) {
        setApplyError('Failed to apply mappings. Please try again.');
        return;
      }
      setResult(res.data);
      // Move successfully applied rows to "already_linked" (Synced tab)
      setRows((prev) =>
        prev.map((row) => {
          const wasApplied = sourceRows.some((sr) => sr.dentallyId === row.dentallyId && sr.actionType !== 'skip');
          if (!wasApplied) return row;
          if (row.actionType === 'create_new' || row.actionType === 'match') {
            return { ...row, actionType: 'already_linked', confidence: 100 };
          }
          return row;
        })
      );
    } catch {
      setApplyError('An unexpected error occurred. Please try again.');
    } finally {
      setApplying(false);
    }
  };

  // Apply all mappings across both Needs Action and Not in CRM tabs
  const handleApplyAll = async () => {
    setApplying(true);
    setApplyError(null);
    const allSourceRows = [...needsActionRows, ...notInCrmRows];
    const mappings = allSourceRows
      .filter((r) => r.actionType !== 'skip' && r.actionType !== 'already_linked')
      .map((r) => {
        if (r.actionType === 'create_new') {
          return { dentally_id: r.dentallyId, create_new: true, name: r.dentallyName };
        }
        return {
          dentally_id: r.dentallyId,
          treatment_id: r.crmTreatment?.id,
          ...(r.alias ? { alias: r.alias } : {}),
        };
      })
      .filter((m) => m.treatment_id || m.create_new);

    if (!mappings.length) { setApplying(false); return; }
    try {
      const res = await callApi(
        `/api/v1/practices/${practiceId}/dentally/apply-treatment-mapping/`,
        'post',
        { mappings }
      );
      if (!res.ok) { setApplyError('Failed to apply all mappings. Please try again.'); return; }
      setResult(res.data);
      setRows((prev) =>
        prev.map((row) => {
          const wasApplied = allSourceRows.some((sr) => sr.dentallyId === row.dentallyId && sr.actionType !== 'skip');
          if (!wasApplied) return row;
          if (row.actionType === 'create_new' || row.actionType === 'match') {
            return { ...row, actionType: 'already_linked', confidence: 100 };
          }
          return row;
        })
      );
    } catch {
      setApplyError('An unexpected error occurred. Please try again.');
    } finally {
      setApplying(false);
    }
  };

  const handleUnlink = useCallback(async (dentallyId, crmTreatmentId) => {
    try {
      await callApi(
        `/api/v1/practices/${practiceId}/dentally/unlink-treatment/`,
        'post',
        { treatment_id: crmTreatmentId }
      );
    } catch {
      // ignore — optimistically update UI anyway
    }
    setRows((prev) =>
      prev.map((row) => {
        if (row.dentallyId !== dentallyId) return row;
        return { ...row, actionType: 'create_new', crmTreatment: null, confidence: 0 };
      })
    );
  }, [practiceId]);

  const handleClose = () => {
    setLoading(true);
    setRows([]);
    setResult(null);
    setApplyError(null);
    setLoadError(null);
    setSearch('');
    setSelectedIds(new Set());
    setReasonMappings({});
    setReasonApplyError(null);
    setReasonApplyResult(null);
    setReasonConfidences({});
    setAiReasonRunning(false);
    setAiReasonUsed(false);
    setInitialReasonMappings({});
    setSelectedReasons(new Set());
    setSkippedReasons(new Set());
    onClose?.();
  };

  const toggleSelect = useCallback((dentallyId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(dentallyId)) next.delete(dentallyId); else next.add(dentallyId);
      return next;
    });
  }, []);

  const handleBulkSkip = () => {
    setRows((prev) =>
      prev.map((row) =>
        selectedIds.has(row.dentallyId) && row.actionType !== 'already_linked'
          ? { ...row, actionType: 'skip' }
          : row
      )
    );
    setSelectedIds(new Set());
  };

  const handleBulkUnlink = async () => {
    const toUnlink = rows.filter((r) => selectedIds.has(r.dentallyId) && r.actionType === 'already_linked');
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Unlink ${toUnlink.length} treatment${toUnlink.length !== 1 ? 's' : ''} from Dentally? This cannot be undone.`)) return;
    setRows((prev) =>
      prev.map((row) =>
        selectedIds.has(row.dentallyId) && row.actionType === 'already_linked'
          ? { ...row, actionType: 'create_new', crmTreatment: null, confidence: 0 }
          : row
      )
    );
    setSelectedIds(new Set());
    // Fire API calls in parallel (non-blocking)
    await Promise.allSettled(
      toUnlink.map((r) =>
        callApi(`/api/v1/practices/${practiceId}/dentally/unlink-treatment/`, 'post', { treatment_id: r.crmTreatment?.id })
      )
    );
  };

  const handleSelectAll = () => {
    const allIds = currentTabRows.map((r) => r.dentallyId);
    const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.has(id));
    setSelectedIds(allSelected ? new Set() : new Set(allIds));
  };

  const handleReasonMappingChange = useCallback((reasonName, treatmentId) => {
    setReasonMappings((prev) => ({ ...prev, [reasonName]: treatmentId || null }));
  }, []);

  const handleApplyReasonMappings = async () => {
    setApplyingReasons(true);
    setReasonApplyError(null);
    setReasonApplyResult(null);

    // Build list of changes: { treatmentId, reasonName (or null for clears) }
    const changes = [];
    // New/changed mappings (exclude skipped)
    Object.entries(reasonMappings).forEach(([reasonName, tid]) => {
      if (tid != null && !skippedReasons.has(reasonName)) {
        const initial = initialReasonMappings[reasonName];
        if (initial !== tid) {
          changes.push({ treatmentId: tid, reasonName });
        }
      }
    });
    // Clears (was mapped before, now removed)
    Object.entries(initialReasonMappings).forEach(([reasonName, tid]) => {
      if (tid != null && (reasonMappings[reasonName] ?? null) === null) {
        changes.push({ treatmentId: tid, reasonName: null });
      }
    });

    if (!changes.length) { setApplyingReasons(false); return; }

    try {
      // PATCH each treatment's dentally_appointment_reason directly (reliable)
      const results = await Promise.allSettled(
        changes.map(({ treatmentId, reasonName }) =>
          callApi(
            `/api/v1/practices/${practiceId}/treatments/${treatmentId}/`,
            'patch',
            { dentally_appointment_reason: reasonName || '' }
          )
        )
      );

      const succeeded = results.filter((r) => r.status === 'fulfilled' && r.value?.ok).length;
      const failed = results.filter((r) => r.status === 'rejected' || !r.value?.ok);

      if (failed.length > 0 && succeeded === 0) {
        setReasonApplyError(`Failed to save reason mappings (${failed.length} error${failed.length !== 1 ? 's' : ''}). Please try again.`);
        return;
      }

      setReasonApplyResult({ updated: succeeded, errors: failed.length > 0 ? [`${failed.length} mapping(s) failed`] : [] });

      // Update local crmTreatments to reflect new reason mappings
      const newMappings = { ...reasonMappings };
      const tidToReason = {};
      Object.entries(newMappings).forEach(([rName, tid]) => {
        if (tid != null) tidToReason[tid] = rName;
      });
      Object.entries(initialReasonMappings).forEach(([rName, tid]) => {
        if (tid != null && (newMappings[rName] ?? null) === null) {
          tidToReason[tid] = null;
        }
      });
      const updatedCrmTreatments = crmTreatments.map(t => ({
        ...t,
        dentally_appointment_reason: tidToReason[t.id] !== undefined ? (tidToReason[t.id] || null) : t.dentally_appointment_reason,
      }));
      setCrmTreatments(updatedCrmTreatments);
      setInitialReasonMappings(newMappings);

      // Update rows: add newly reason-synced treatments to Synced tab
      setRows((prev) => {
        const existingIds = new Set(prev.map((r) => r.crmTreatment?.id));
        const newRows = [...prev];
        // Add new reason-synced treatments that aren't already in rows
        changes.forEach(({ treatmentId, reasonName: rName }) => {
          if (rName && !existingIds.has(treatmentId)) {
            const crm = updatedCrmTreatments.find((t) => t.id === treatmentId);
            if (crm) {
              newRows.push({
                dentallyId: `reason_${treatmentId}`,
                dentallyName: rName,
                crmTreatment: { id: crm.id, name: crm.name, dentally_treatment_id: crm.dentally_treatment_id || '', dentally_appointment_reason: rName },
                actionType: 'already_linked',
                alias: '',
                confidence: 100,
              });
            }
          }
        });
        // Update existing rows that got a reason change
        return newRows.map((row) => {
          const rName = tidToReason[row.crmTreatment?.id];
          if (rName !== undefined && row.crmTreatment) {
            return { ...row, crmTreatment: { ...row.crmTreatment, dentally_appointment_reason: rName || null } };
          }
          return row;
        });
      });
    } catch {
      setReasonApplyError('An unexpected error occurred. Please try again.');
    } finally {
      setApplyingReasons(false);
    }
  };

  const handleAiReasonMatch = useCallback(async () => {
    if (!reasons.length || !crmTreatments.length) return;
    setAiReasonRunning(true);
    try {
      const aiMatches = await fetchAiReasonMatches(practiceId, reasons, crmTreatments);
      if (!aiMatches) return;
      const crmById = Object.fromEntries(crmTreatments.map((c) => [c.id, c]));
      const newMappings = { ...reasonMappings };
      const newConfidences = {};
      aiMatches.forEach(({ reason_index: idx, crm_id: crmId, confidence }) => {
        const r = reasons[idx];
        if (!r) return;
        const reasonName = r.name || r.reason || String(r);
        // Only suggest if not already mapped and not skipped
        if (!newMappings[reasonName] && !skippedReasons.has(reasonName) && crmId && crmById[crmId]) {
          newMappings[reasonName] = crmId;
          newConfidences[reasonName] = confidence ?? 0;
        }
      });
      setReasonMappings(newMappings);
      setReasonConfidences(newConfidences);
      setAiReasonUsed(true);
    } catch {
      // silently fall back
    } finally {
      setAiReasonRunning(false);
    }
  }, [reasons, crmTreatments, reasonMappings, skippedReasons, practiceId]);

  const allCurrentSelected = currentTabRows.length > 0 && currentTabRows.every((r) => selectedIds.has(r.dentallyId));
  const someCurrentSelected = selectedIds.size > 0 && !allCurrentSelected;

  // Only count mappings from the active tab
  const pendingCount = useMemo(() => {
    if (activeTab === 0) return 0; // Synced tab has no apply action
    if (activeTab === 1) return needsActionRows.filter((r) => r.actionType === 'match').length;
    if (activeTab === 2) return notInCrmRows.length;
    return 0;
  }, [activeTab, needsActionRows, notInCrmRows]);

  // Total pending across both treatment tabs (for "Apply All" button)
  const needsActionPending = useMemo(() => needsActionRows.filter((r) => r.actionType === 'match').length, [needsActionRows]);
  const notInCrmPending = useMemo(() => notInCrmRows.filter((r) => r.actionType !== 'skip').length, [notInCrmRows]);
  const totalPendingCount = needsActionPending + notInCrmPending;
  // Only show Apply All when both tabs have pending items
  const showApplyAll = needsActionPending > 0 && notInCrmPending > 0;

  // Count only CHANGED reason mappings for the Save button
  const pendingReasonCount = useMemo(() => {
    let count = 0;
    // New or changed mappings
    Object.entries(reasonMappings).forEach(([rName, tid]) => {
      if (tid != null && !skippedReasons.has(rName) && tid !== (initialReasonMappings[rName] ?? null)) count += 1;
    });
    // Cleared mappings
    Object.entries(initialReasonMappings).forEach(([rName, tid]) => {
      if (tid != null && (reasonMappings[rName] ?? null) === null) count += 1;
    });
    return count;
  }, [reasonMappings, initialReasonMappings, skippedReasons]);

  // Count how many reasons are mapped (for display in summary card)
  const mappedReasonCount = useMemo(
    () => Object.values(reasonMappings).filter((v) => v != null).length,
    [reasonMappings]
  );

  // Reverse lookup: crmTreatmentId → reason name (for showing reason on synced rows)
  const treatmentIdToReason = useMemo(() => {
    const map = {};
    Object.entries(reasonMappings).forEach(([reasonName, tid]) => {
      if (tid != null) map[tid] = reasonName;
    });
    return map;
  }, [reasonMappings]);

  return (
    <MappingShell inline={inline} open={open} onClose={handleClose} maxWidth="md">
      <MappingShellTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Iconify icon="mdi:swap-horizontal-bold" width={22} sx={{ color: '#00AB55' }} />
          <Typography variant="h6" fontWeight={700}>Sync Dentally Treatments</Typography>
        </Stack>
        {!inline && (
          <IconButton size="small" onClick={handleClose}>
            <Iconify icon="mdi:close" width={20} />
          </IconButton>
        )}
      </MappingShellTitle>
      <MappingShellContent dividers>
        {/* Loading (initial load only — resyncing shows inline) */}
        {loading && !resyncing && !loadError && (
          <Stack alignItems="center" spacing={2} py={5}>
            <CircularProgress />
            <Typography color="text.secondary">{loadingStatus || 'Loading treatments…'}</Typography>
          </Stack>
        )}
        {loadError && <Alert severity="error">{loadError}</Alert>}

        {/* Result banner */}
        {result && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setResult(null)}>
            Sync complete — {result.linked || 0} linked, {result.created || 0} created
            {result.aliases_created > 0 && `, ${result.aliases_created} aliases`}
            {result.errors?.length > 0 && `. ${result.errors.length} error(s): ${result.errors.join('; ')}`}
          </Alert>
        )}

        {/* Main content */}
        {!loading && !loadError && (
          <>
            {/* Re-sync + AI Match buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1.5 }}>
              <LoadingButton
                size="small"
                variant="outlined"
                loading={resyncing && !aiMatchingUsed}
                startIcon={<Iconify icon="mdi:refresh" />}
                onClick={() => runFullSync(crmTreatments)}
                sx={{ textTransform: 'none' }}
              >
                Re-sync from Dentally
              </LoadingButton>
              <LoadingButton
                size="small"
                variant="outlined"
                loading={resyncing && loadingStatus.includes('AI')}
                startIcon={<Iconify icon="mdi:robot-outline" />}
                onClick={runAiMatching}
                disabled={resyncing || needsActionRows.length + notInCrmRows.length === 0}
                sx={{ textTransform: 'none' }}
              >
                AI Match
              </LoadingButton>
            </Box>

            {resyncing && (
              <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ mb: 2 }}>
                {loadingStatus || 'Re-syncing from Dentally…'}
              </Alert>
            )}
            {applyError && <Alert severity="error" sx={{ mb: 2 }}>{applyError}</Alert>}
            {aiMatchingUsed && (
              <Alert
                severity="info"
                icon={<Iconify icon="mdi:robot-outline" width={18} />}
                sx={{ mb: 2, py: 0.5 }}
              >
                Matches suggested by AI — review and adjust before applying.
              </Alert>
            )}

            {/* Summary Cards */}
            <Stack direction="row" spacing={1.5} mb={2}>
              <SummaryCard
                icon="mdi:check-circle"
                color="#00AB55"
                count={syncedRows.length}
                label="Synced"
                active={activeTab === 0}
                onClick={() => setActiveTab(0)}
              />
              <SummaryCard
                icon="mdi:alert-circle"
                color="#FFAB00"
                count={needsActionRows.length}
                label="Needs Action"
                active={activeTab === 1}
                onClick={() => setActiveTab(1)}
              />
              <SummaryCard
                icon="mdi:plus-circle"
                color="#078DEE"
                count={notInCrmRows.length}
                label="Not in CRM"
                active={activeTab === 2}
                onClick={() => setActiveTab(2)}
              />
              <SummaryCard
                icon="mdi:calendar-clock-outline"
                color="#855CF8"
                count={reasons.length}
                label={`Reasons (${mappedReasonCount} mapped)`}
                active={activeTab === 3}
                onClick={() => setActiveTab(3)}
              />
            </Stack>

            {/* Search + Bulk actions (treatment tabs only) */}
            {activeTab !== 3 && <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
              <TextField
                size="small"
                placeholder="Search treatments…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify icon="mdi:magnify" width={18} />
                    </InputAdornment>
                  ),
                }}
              />
              {selectedIds.size > 0 && activeTab === 0 && (
                <Button size="small" color="warning" variant="outlined" onClick={handleBulkUnlink} sx={{ textTransform: 'none' }}>
                  Unlink {selectedIds.size} selected
                </Button>
              )}
              {selectedIds.size > 0 && activeTab === 1 && (
                <Button size="small" color="warning" variant="outlined" onClick={handleBulkSkip} sx={{ textTransform: 'none' }}>
                  Skip {selectedIds.size} selected
                </Button>
              )}
            </Stack>}

            {/* Tabs */}
            <Tabs
              value={activeTab}
              onChange={(_, v) => { setActiveTab(v); setSelectedIds(new Set()); }}
              sx={{ mb: 1, minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5, textTransform: 'none', fontSize: 13 } }}
            >
              <Tab label={`Synced (${syncedRows.length})`} />
              <Tab label={`Needs Action (${needsActionRows.length})`} />
              <Tab label={`Not in CRM (${notInCrmRows.length})`} />
              <Tab label={`Reasons${reasons.length ? ` (${reasons.length})` : ''}`} />
            </Tabs>

            {/* Reasons tab */}
            {activeTab === 3 ? (
              <Box sx={{ maxHeight: 380, overflowY: 'auto' }}>
                {reasonsLoading && (
                  <Stack alignItems="center" py={4} spacing={1.5}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" color="text.secondary">Loading appointment reasons…</Typography>
                  </Stack>
                )}
                {reasonsError && <Alert severity="error">{reasonsError}</Alert>}
                {reasonApplyError && <Alert severity="error" sx={{ mb: 1 }}>{reasonApplyError}</Alert>}
                {reasonApplyResult && (
                  <Alert severity="success" sx={{ mb: 1 }} onClose={() => setReasonApplyResult(null)}>
                    {reasonApplyResult.updated} reason mapping{reasonApplyResult.updated !== 1 ? 's' : ''} saved.
                    {reasonApplyResult.errors?.length > 0 && ` ${reasonApplyResult.errors.length} error(s).`}
                  </Alert>
                )}
                {!reasonsLoading && !reasonsError && reasons.length === 0 && (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">No appointment reasons found in Dentally.</Typography>
                  </Box>
                )}
                {!reasonsLoading && !reasonsError && reasons.length > 0 && (
                  <>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
                      <Typography variant="body2" color="text.secondary">
                        Map each appointment reason to a CRM treatment for auto-fill on booking.
                      </Typography>
                      <LoadingButton
                        size="small"
                        variant="outlined"
                        loading={aiReasonRunning}
                        startIcon={<Iconify icon="mdi:robot-outline" />}
                        onClick={handleAiReasonMatch}
                        disabled={reasonsLoading || !reasons.length}
                        sx={{ textTransform: 'none', flexShrink: 0 }}
                      >
                        AI Match
                      </LoadingButton>
                    </Stack>
                    {aiReasonUsed && (
                      <Alert severity="info" icon={<Iconify icon="mdi:robot-outline" width={18} />} sx={{ mb: 1.5, py: 0.5 }}>
                        AI suggestions applied — review and adjust before saving.
                      </Alert>
                    )}
                    {selectedReasons.size > 0 && (
                      <Stack direction="row" spacing={1} mb={1}>
                        <Button
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ textTransform: 'none' }}
                          onClick={() => {
                            setSkippedReasons((prev) => {
                              const next = new Set(prev);
                              selectedReasons.forEach((rName) => next.add(rName));
                              return next;
                            });
                            setReasonMappings((prev) => {
                              const next = { ...prev };
                              selectedReasons.forEach((rName) => { next[rName] = null; });
                              return next;
                            });
                            setSelectedReasons(new Set());
                          }}
                        >
                          Skip {selectedReasons.size} selected
                        </Button>
                        <Button
                          size="small"
                          color="inherit"
                          variant="outlined"
                          sx={{ textTransform: 'none' }}
                          onClick={() => {
                            setReasonMappings((prev) => {
                              const next = { ...prev };
                              selectedReasons.forEach((rName) => { next[rName] = null; });
                              return next;
                            });
                            setSelectedReasons(new Set());
                          }}
                        >
                          Clear {selectedReasons.size} selected
                        </Button>
                      </Stack>
                    )}
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              indeterminate={selectedReasons.size > 0 && selectedReasons.size < reasons.length}
                              checked={reasons.length > 0 && selectedReasons.size === reasons.length}
                              onChange={() => {
                                if (selectedReasons.size === reasons.length) {
                                  setSelectedReasons(new Set());
                                } else {
                                  setSelectedReasons(new Set(reasons.map((r) => r.name || r.reason || String(r))));
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell>Appointment Reason</TableCell>
                          <TableCell>CRM Treatment (auto-fill on booking)</TableCell>
                          <TableCell width={80}>Status</TableCell>
                          <TableCell width={72}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reasons.map((r, i) => {
                          const reasonName = r.name || r.reason || String(r);
                          const mappedId = reasonMappings[reasonName] ?? null;
                          const initialId = initialReasonMappings[reasonName] ?? null;
                          const selectedTreatment = crmTreatments.find((t) => t.id === mappedId) || null;
                          const isSaved = mappedId != null && mappedId === initialId;
                          const isChanged = mappedId !== initialId;
                          const isSkipped = skippedReasons.has(reasonName);
                          return (
                            <TableRow key={r.id || i} sx={{ ...(isChanged ? { bgcolor: 'action.hover' } : {}), opacity: isSkipped ? 0.4 : 1 }}>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  size="small"
                                  checked={selectedReasons.has(reasonName)}
                                  onChange={() => {
                                    setSelectedReasons((prev) => {
                                      const next = new Set(prev);
                                      if (next.has(reasonName)) next.delete(reasonName); else next.add(reasonName);
                                      return next;
                                    });
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Iconify icon="mdi:calendar-clock-outline" width={15} sx={{ color: '#637381', flexShrink: 0 }} />
                                  <Typography variant="body2">{reasonName}</Typography>
                                  {reasonConfidences[reasonName] > 0 && (
                                    <Chip
                                      label={`${reasonConfidences[reasonName]}%`}
                                      size="small"
                                      color={(() => {
                                        const conf = reasonConfidences[reasonName];
                                        if (conf >= 80) return 'success';
                                        if (conf >= 50) return 'warning';
                                        return 'default';
                                      })()}
                                    />
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell sx={{ minWidth: 220 }}>
                                {isSkipped ? (
                                  <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>Skipped</Typography>
                                ) : (
                                  <Autocomplete
                                    size="small"
                                    options={crmTreatments}
                                    value={selectedTreatment}
                                    getOptionLabel={(opt) => opt.name || ''}
                                    isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                                    onChange={(_, v) => handleReasonMappingChange(reasonName, v?.id ?? null)}
                                    renderInput={(params) => (
                                      <TextField {...params} placeholder="Select CRM treatment…" />
                                    )}
                                    clearOnEscape
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                {isSkipped && <Chip size="small" label="Skipped" color="default" variant="outlined" sx={{ fontSize: 11 }} />}
                                {!isSkipped && isSaved && <Chip size="small" label="Saved" color="success" variant="outlined" sx={{ fontSize: 11 }} />}
                                {!isSkipped && isChanged && mappedId != null && <Chip size="small" label="New" color="info" variant="outlined" sx={{ fontSize: 11 }} />}
                                {!isSkipped && isChanged && mappedId == null && initialId != null && <Chip size="small" label="Cleared" color="warning" variant="outlined" sx={{ fontSize: 11 }} />}
                                {!isSkipped && !isSaved && !isChanged && <Chip size="small" label="Unmapped" color="default" variant="outlined" sx={{ fontSize: 11 }} />}
                              </TableCell>
                              <TableCell>
                                <Button
                                  size="small"
                                  color={isSkipped ? 'primary' : 'inherit'}
                                  sx={{ textTransform: 'none', fontSize: 12, minWidth: 48 }}
                                  onClick={() => {
                                    if (isSkipped) {
                                      setSkippedReasons((prev) => { const next = new Set(prev); next.delete(reasonName); return next; });
                                    } else {
                                      setSkippedReasons((prev) => new Set(prev).add(reasonName));
                                      handleReasonMappingChange(reasonName, null);
                                    }
                                  }}
                                >
                                  {isSkipped ? 'Undo' : 'Skip'}
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </>
                )}
              </Box>
            ) : (
              /* Treatment tabs (0, 1, 2) */
              <Box sx={{ maxHeight: 380, overflowY: 'auto' }}>
                {currentTabRows.length === 0 ? (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {search.trim() ? 'No treatments match your search.' : 'No treatments in this category.'}
                    </Typography>
                  </Box>
                ) : (
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            indeterminate={someCurrentSelected}
                            checked={allCurrentSelected}
                            onChange={handleSelectAll}
                          />
                        </TableCell>
                        <TableCell>Dentally Treatment</TableCell>
                        {activeTab === 0 ? (
                          <>
                            <TableCell>Linked CRM Treatment</TableCell>
                            <TableCell>Synced</TableCell>
                          </>
                        ) : (
                          <TableCell>CRM Treatment</TableCell>
                        )}
                        {activeTab === 1 && <TableCell>Alias</TableCell>}
                        <TableCell width={72}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentTabRows.map((row) => {
                        if (activeTab === 0) {
                          return (
                            <SyncedRow
                              key={row.dentallyId}
                              row={row}
                              onUnlink={handleUnlink}
                              selected={selectedIds.has(row.dentallyId)}
                              onToggleSelect={toggleSelect}
                              reasonName={treatmentIdToReason[row.crmTreatment?.id] || null}
                            />
                          );
                        }
                        if (activeTab === 2) {
                          return (
                            <NotInCrmRow
                              key={row.dentallyId}
                              row={row}
                              crmTreatments={crmTreatments}
                              onUpdate={updateRow}
                              selected={selectedIds.has(row.dentallyId)}
                              onToggleSelect={toggleSelect}
                            />
                          );
                        }
                        return (
                          <NeedsActionRow
                            key={row.dentallyId}
                            row={row}
                            crmTreatments={crmTreatments}
                            onUpdate={updateRow}
                            selected={selectedIds.has(row.dentallyId)}
                            onToggleSelect={toggleSelect}
                          />
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </Box>
            )}
          </>
        )}
      </MappingShellContent>

      <MappingShellActions sx={{ px: 3, py: 1.5 }}>
        {!inline && <Button onClick={handleClose} color="inherit">Close</Button>}
        {!loading && !loadError && activeTab !== 3 && pendingCount > 0 && (
          <LoadingButton
            variant="contained"
            loading={applying}
            onClick={handleApply}
            startIcon={<Iconify icon="mdi:check-all" />}
          >
            Apply {pendingCount} Mapping{pendingCount !== 1 ? 's' : ''}
          </LoadingButton>
        )}
        {!loading && !loadError && activeTab !== 3 && showApplyAll && (
          <LoadingButton
            variant="outlined"
            loading={applying}
            onClick={handleApplyAll}
            startIcon={<Iconify icon="mdi:check-all" />}
            sx={{ textTransform: 'none' }}
          >
            Apply All ({totalPendingCount})
          </LoadingButton>
        )}
        {activeTab === 3 && !reasonsLoading && pendingReasonCount > 0 && (
          <LoadingButton
            variant="contained"
            loading={applyingReasons}
            onClick={handleApplyReasonMappings}
            startIcon={<Iconify icon="mdi:content-save-outline" />}
          >
            Save {pendingReasonCount} Change{pendingReasonCount !== 1 ? 's' : ''}
          </LoadingButton>
        )}
      </MappingShellActions>
    </MappingShell>
  );
}

DentallyTreatmentSyncWizard.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  inline: PropTypes.bool,
  practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

DentallyTreatmentSyncWizard.defaultProps = {
  open: false,
  onClose: undefined,
  inline: false,
  practiceId: undefined,
};

// ─── Synced row ──────────────────────────────────────────────────────────────

function SyncedRow({ row, onUnlink, selected, onToggleSelect, reasonName }) {
  const hasTreatmentSync = row.crmTreatment?.dentally_treatment_id && row.crmTreatment.dentally_treatment_id.trim();
  const hasReasonSync = reasonName || row.crmTreatment?.dentally_appointment_reason;
  const displayReason = reasonName || row.crmTreatment?.dentally_appointment_reason || null;

  return (
    <TableRow>
      <TableCell padding="checkbox">
        <Checkbox size="small" checked={selected} onChange={() => onToggleSelect(row.dentallyId)} />
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          <Iconify
            icon={hasTreatmentSync ? 'mdi:check-circle' : 'mdi:calendar-clock-outline'}
            width={16}
            sx={{ color: hasTreatmentSync ? '#00AB55' : '#855CF8' }}
          />
          <Typography variant="body2">{row.dentallyName}</Typography>
        </Stack>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{row.crmTreatment?.name}</Typography>
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
          {hasTreatmentSync && (
            <Chip size="small" label="Treatment" color="success" variant="outlined" sx={{ fontSize: 11 }} />
          )}
          {hasReasonSync
            ? <Tooltip title={`Appointment reason: ${displayReason}`}>
                <Chip size="small" label={displayReason} color="success" variant="outlined" sx={{ fontSize: 11, maxWidth: 150 }} />
              </Tooltip>
            : !hasTreatmentSync && <Chip size="small" label="Reason only" color="info" variant="outlined" sx={{ fontSize: 11 }} />
          }
        </Stack>
      </TableCell>
      <TableCell>
        {hasTreatmentSync && (
          <Tooltip title="Remove Dentally link from this treatment">
            <Button
              size="small"
              color="inherit"
              sx={{ textTransform: 'none', fontSize: 12 }}
              onClick={() => onUnlink(row.dentallyId, row.crmTreatment?.id)}
            >
              Unlink
            </Button>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
}

SyncedRow.propTypes = {
  row: PropTypes.object.isRequired,
  onUnlink: PropTypes.func.isRequired,
  selected: PropTypes.bool.isRequired,
  onToggleSelect: PropTypes.func.isRequired,
  reasonName: PropTypes.string,
};

// ─── Needs Action row ────────────────────────────────────────────────────────

function NeedsActionRow({ row, crmTreatments, onUpdate, selected, onToggleSelect }) {
  const isSkipped = row.actionType === 'skip';

  const handleCrmChange = (_, newValue) => {
    if (!newValue) {
      onUpdate(row.dentallyId, { crmTreatment: null, actionType: 'skip', alias: '' });
      return;
    }
    if (newValue.isCreate) {
      onUpdate(row.dentallyId, { crmTreatment: null, actionType: 'create_new', alias: '' });
      return;
    }
    const alias =
      newValue.name.toLowerCase() !== row.dentallyName.toLowerCase() ? row.dentallyName : '';
    onUpdate(row.dentallyId, { crmTreatment: newValue, actionType: 'match', alias });
  };

  return (
    <TableRow sx={{ opacity: isSkipped ? 0.4 : 1 }}>
      <TableCell padding="checkbox">
        <Checkbox size="small" checked={selected} onChange={() => onToggleSelect(row.dentallyId)} />
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2">{row.dentallyName}</Typography>
          {!isSkipped && row.confidence > 0 && (
            <Chip
              label={`${row.confidence}%`}
              size="small"
              color={(() => { if (row.confidence >= 80) return 'success'; if (row.confidence >= 50) return 'warning'; return 'default'; })()}
            />
          )}
        </Stack>
      </TableCell>

      <TableCell sx={{ minWidth: 200 }}>
        <Autocomplete
          size="small"
          options={crmTreatments}
          value={row.crmTreatment || null}
          getOptionLabel={(opt) => opt.name || ''}
          isOptionEqualToValue={(opt, val) => opt.id === val?.id}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);
            if (params.inputValue) {
              filtered.push({
                id: 'CREATE_NEW',
                name: `+ Create "${params.inputValue}"`,
                isCreate: true,
              });
            }
            return filtered;
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Typography variant="body2" color={option.isCreate ? 'primary' : 'text.primary'}>
                {option.name}
              </Typography>
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} placeholder="Search CRM treatments…" />
          )}
          onChange={handleCrmChange}
          clearOnEscape
        />
      </TableCell>

      <TableCell>
        {row.actionType === 'match' && (
          <TextField
            size="small"
            value={row.alias}
            onChange={(e) => onUpdate(row.dentallyId, { alias: e.target.value })}
            placeholder="Alias"
            sx={{ width: 130 }}
          />
        )}
      </TableCell>

      <TableCell>
        <Button
          size="small"
          color={isSkipped ? 'primary' : 'inherit'}
          sx={{ textTransform: 'none', minWidth: 48 }}
          onClick={() => {
            if (isSkipped) {
              // Restore to match (if has suggestion) or back to unmatched state in needs-action
              onUpdate(row.dentallyId, { actionType: row.crmTreatment ? 'match' : 'match' });
            } else {
              onUpdate(row.dentallyId, { actionType: 'skip' });
            }
          }}
        >
          {isSkipped ? 'Undo' : 'Skip'}
        </Button>
      </TableCell>
    </TableRow>
  );
}

NeedsActionRow.propTypes = {
  row: PropTypes.object.isRequired,
  crmTreatments: PropTypes.array.isRequired,
  onUpdate: PropTypes.func.isRequired,
  selected: PropTypes.bool.isRequired,
  onToggleSelect: PropTypes.func.isRequired,
};

// ─── Not in CRM row ─────────────────────────────────────────────────────────

function NotInCrmRow({ row, crmTreatments, onUpdate, selected, onToggleSelect }) {
  const handleCrmChange = (_, newValue) => {
    if (!newValue) {
      onUpdate(row.dentallyId, { crmTreatment: null, actionType: 'create_new', alias: '' });
      return;
    }
    const alias =
      newValue.name.toLowerCase() !== row.dentallyName.toLowerCase() ? row.dentallyName : '';
    onUpdate(row.dentallyId, { crmTreatment: newValue, actionType: 'match', alias });
  };

  return (
    <TableRow>
      <TableCell padding="checkbox">
        <Checkbox size="small" checked={selected} onChange={() => onToggleSelect(row.dentallyId)} />
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          <Iconify icon="mdi:plus-circle-outline" width={16} sx={{ color: '#078DEE' }} />
          <Typography variant="body2">{row.dentallyName}</Typography>
        </Stack>
      </TableCell>

      <TableCell sx={{ minWidth: 200 }}>
        <Autocomplete
          size="small"
          options={crmTreatments}
          value={row.actionType === 'match' ? (row.crmTreatment || null) : null}
          getOptionLabel={(opt) => opt.name || ''}
          isOptionEqualToValue={(opt, val) => opt.id === val?.id}
          renderInput={(params) => (
            <TextField {...params} placeholder="Link to existing, or will create new" />
          )}
          onChange={handleCrmChange}
          clearOnEscape
        />
      </TableCell>

      <TableCell>
        <Chip
          label={row.actionType === 'match' ? 'Will link' : 'Will create'}
          size="small"
          color={row.actionType === 'match' ? 'success' : 'info'}
          variant="outlined"
        />
      </TableCell>
    </TableRow>
  );
}

NotInCrmRow.propTypes = {
  row: PropTypes.object.isRequired,
  crmTreatments: PropTypes.array.isRequired,
  onUpdate: PropTypes.func.isRequired,
  selected: PropTypes.bool.isRequired,
  onToggleSelect: PropTypes.func.isRequired,
};
